import React, { useState, useEffect } from "react";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { useOrganizations } from "@/services/organizations.services";
import { RoundLoader } from "@/common/ui/Loader";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import { Button } from "@/components/ui/button";
import { Center } from "@/common/css";
import Icon, { Image } from "@/common/ui/Image";
import { FiSearch } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import EmptyList from "@/components/common/EmptyList";
import Pagination from "@/common/Table/ItemsPerPage";
import SimpleTableView from "@/components/common/table/SimpleTableView";
import { ColumnDef } from "@tanstack/react-table";
import CreateOrganizationModal from "@/components/internal/organizations/CreateOrganizationModal";
import UpdateOrganizationModal from "@/components/internal/organizations/UpdateOrganizationModal";
import DeleteOrganizationModal from "@/components/internal/organizations/DeleteOrganizationModal";
import { MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LP_FE_BASE_URL } from "@/utils/constant/globalEnv.constant";

interface Organization {
  id: number;
  name: string;
  slug: string;
  description?: string;
  org_type?: string;
  [key: string]: any;
}

const modalConfig: ModalConfig = {
  create: {
    component: CreateOrganizationModal,
    title: "Create Organization",
    type: "side",
    modalClassName: "max-w-[600px]",
  },
  update: {
    component: UpdateOrganizationModal,
    title: "Update Organization",
    type: "side",
    modalClassName: "max-w-[600px]",
  },
  delete: {
    component: DeleteOrganizationModal,
    title: "Delete Organization",
    type: "center",
    modalClassName: "max-w-[400px]",
  },
};

interface OrganizationsPageProps {
  openModal: (modalKey: string, props?: any) => void;
  closeModal: () => void;
  activeModal: string;
}

const OrganizationsPage: React.FC<OrganizationsPageProps> = ({ openModal, activeModal: _activeModal }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState({ value: 12, label: `Records per page ${12}` });
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  const {
    data: organizationsData,
    isLoading,
    isFetching,
    refetch,
  } = useOrganizations({
    search: searchQuery,
    sortBy,
    sortOrder,
    page: currentPage,
    per_page: perPage.value,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSearch = (q: string) => {
    setSearchInput(q);
    if (!q) {
      setSearchQuery("");
      setCurrentPage(1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchQuery(searchInput);
      setCurrentPage(1);
      refetch();
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(field);
      setSortOrder("ASC");
    }
  };

  const handleCreate = () => {
    openModal("create", { afterCall: refetch });
  };

  const handleUpdate = (org: { id: string | number; [key: string]: any }) => {
    // Convert string id back to number for the API
    const orgWithNumberId: Organization = {
      ...org,
      id: typeof org.id === 'string' ? parseInt(org.id) : org.id,
    } as Organization;
    openModal("update", { organization: orgWithNumberId, afterCall: refetch });
  };

  const handleDelete = (org: { id: string | number; [key: string]: any }) => {
    // Convert string id back to number for the API
    const orgWithNumberId: Organization = {
      ...org,
      id: typeof org.id === 'string' ? parseInt(org.id) : org.id,
    } as Organization;
    openModal("delete", { organization: orgWithNumberId, afterCall: refetch });
  };

  const organizations = organizationsData?.data || [];
  const totalPages = organizationsData?.totalPages || 1;

  // Convert organizations to have string ids for SimpleTableView
  type OrganizationWithStringId = Omit<Organization, 'id'> & { id: string };
  const organizationsWithStringIds: OrganizationWithStringId[] = organizations.map((org) => ({ ...org, id: org.id.toString() }));

  const columns: ColumnDef<OrganizationWithStringId>[] = [
    {
      accessorKey: "name",
      header: () => (
        <div
          onClick={() => handleSort("name")}
          className="flex items-center gap-1 hover:text-text-primary cursor-pointer"
        >
          Name
          {sortBy === "name" && (
            <span className="text-xs">{sortOrder === "ASC" ? "↑" : "↓"}</span>
          )}
        </div>
      ),
      cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
    },
    {
      accessorKey: "slug",
      header: "Url",
      cell: ({ row }) => {
        const org = row.original;
        const orgType = org.org_type?.toLowerCase() || "";
        const url = `${LP_FE_BASE_URL}/${orgType}/${org.slug}`;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-primary hover:underline"
          >
            {url}
          </a>
        );
      },
    },
    {
      accessorKey: "org_type",
      header: "Type",
      cell: ({ row }) => <div className="text-text-default">{row.original.org_type || "N/A"}</div>,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => (
        <div className="text-text-default max-w-[300px] truncate">
          {row.original.description || "N/A"}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const org = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleUpdate(org)}>Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDelete(org)} className="text-red-600">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col flex-1 h-full w-full">
      <SideBarContentHeader className="!pb-3.5 !pt-0 px-4">
        <div className="flex justify-between w-full items-center">
          <h2 className="text-lg font-semibold text-text-default">Organizations</h2>
          <div className="flex items-center gap-2">
            <div className="!p-0 !min-w-0" onClick={() => refetch()}>
              <Icon
                src="/icons/refresh.svg"
                alt="refresh icon"
                className={`${isLoading || isFetching ? "animate-spin-reverse" : ""}`}
              />
            </div>
            <div className="relative m-1">
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchInput}
                onChange={(e: any) => {
                  let value = e.target.value;
                  if (value.includes("%") || value.length > 50) return;
                  if (value.length > 0 && value.trim().length === 0) return;
                  if (value?.trim().length === 0) {
                    value = value.trim();
                  }
                  handleSearch(value);
                }}
                onKeyDown={handleKeyDown}
                className="pl-3 pr-[54px] py-1.5 md:w-60 rounded-md border border-gray-300 text-13 !leading-normal outline-none focus:ring-1 focus:ring-text-primary placeholder:text-gray-400"
              />
              <Center className="absolute right-3 top-1/2 -translate-y-1/2">
                {searchInput?.length > 0 ? (
                  <Center className="gap-1">
                    <RxCross2
                      className="size-5 text-text-default hover:cursor-pointer"
                      onClick={() => handleSearch("")}
                    />
                    <Image
                      src="/icons/search.svg"
                      alt=""
                      className="size-5 hover:cursor-pointer"
                      onClick={() => {
                        setSearchQuery(searchInput);
                        setCurrentPage(1);
                        refetch();
                      }}
                    />
                  </Center>
                ) : (
                  <FiSearch className="text-text-primary" />
                )}
              </Center>
            </div>
            <Button onClick={handleCreate}>
              Create Organization
            </Button>
          </div>
        </div>
      </SideBarContentHeader>

      <div className="flex flex-col flex-1 min-h-0">
        {isLoading ? (
          <Center className="min-h-[300px] h-full">
            <RoundLoader />
          </Center>
        ) : !isFetching && organizations.length === 0 ? (
          <EmptyList
            text={searchQuery ? "No organizations found." : "No organizations yet."}
            icon="/icons/empty-list-file.svg"
            className="h-full gap-y-4"
          >
            {!searchQuery && (
              <Button onClick={handleCreate}>
                Create Organization
              </Button>
            )}
          </EmptyList>
        ) : (
          <>
            <div className="flex-1 overflow-auto">
              <SimpleTableView
                data={organizationsWithStringIds}
                columnsConfig={columns}
                isLoading={isFetching}
                externalPagination={{
                  currentPage,
                  totalPages,
                  onPageChange: (page: number) => setCurrentPage(page),
                  perPage,
                  setPerPage: (count: { value: number; label: string }) => {
                    setPerPage(count);
                    setCurrentPage(1);
                  },
                }}
                paginationConfig={{
                  initialPage: currentPage,
                  initialPerPage: perPage.value,
                  perPageOptions: [10, 12, 25, 50, 100],
                }}
                hidePagination={true}
              />
            </div>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              perPage={perPage}
              perPageOptions={[10, 12, 25, 50, 100]}
              onPageChange={(page: number) => setCurrentPage(page)}
              onPerPageChange={(count: { value: number; label: string }) => {
                setPerPage(count);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const OrganizationsPageWithModal = withModal(OrganizationsPage, modalConfig);

